import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ChevronLeft, Download, Send, MessageSquarePlus, Check, X, Truck, AlertTriangle, CircleCheck, Copy, Trash2, Ban } from "lucide-react";
import ConfirmAction from "@/components/ConfirmAction";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import ChatPanel from "@/components/chat/ChatPanel";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RESPONSE_TYPES, responseTypeMeta } from "@/constants/vendorResponses";
import { PURCHASING_ROLES } from "@/constants/roles";
import DocumentsPanel from "@/components/DocumentsPanel";
import ReceivingPanel from "@/components/ReceivingPanel";
import { fmtDate, fmtDateTime } from "@/lib/utils";
import SearchableSelect from "@/components/SearchableSelect";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

// A PO whose lines can no longer change tax treatment (backend PO_TAX_LOCKED_STATUSES).
const TAX_LOCKED = ["invoiced", "closed", "cancelled", "rejected"];
const emptyManualForm = { type: "acknowledged", lineRef: "", lineRefs: [], newPrice: "", proposedItem: "", estimatedDate: "", date: "", note: "", trackingNumber: "", carrier: "", shipDate: "" };

export default function PurchaseOrderDetail() {
  const { id } = useParams();
  const [po, setPo] = useState(null);
  const [vendorInfo, setVendorInfo] = useState(null);
  const [showSend, setShowSend] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const [sending, setSending] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [manualForm, setManualForm] = useState(emptyManualForm);
  const [showDelivery, setShowDelivery] = useState(false);
  const [deliveryForm, setDeliveryForm] = useState({ expected_delivery: "", delivery_instructions: "", site_contact_name: "", site_contact_phone: "" });
  const [savingDelivery, setSavingDelivery] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [confirmRemove, setConfirmRemove] = useState(null); // delete | cancel

  const load = () => {
    api.get(`/contractor/purchase-orders/${id}`).then((r) => setPo(r.data));
    api.get("/contractor/invoices", { params: { po_id: id } }).then((r) => setInvoices(r.data));
  };
  useEffect(() => {
    load();
    api.get("/contractor/vendor-network").then((r) => setVendorInfo(r.data));
  }, [id]);

  if (!po || !vendorInfo) return <div className="p-8 text-slate-400 text-sm">Loading…</div>;

  const vendorEntry = vendorInfo.find((v) => v.vendor_id === po.vendor_id);
  const canManagePO = PURCHASING_ROLES.includes(user?.role);
  const setRamp = async (on) => {
    try {
      await api.patch(`/contractor/purchase-orders/${id}/payment-method`, { payment_method: on ? "ramp" : null });
      load(); // the page's PO carries the job name, which the bare PATCH result doesn't
      toast.success(on ? "This PO will be paid with Ramp" : "Ramp payment removed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't change how this PO is paid");
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/contractor/purchase-orders/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (e) {
      toast.error("Failed to load PDF");
    }
  };

  const setLineTaxable = async (i, taxable) => {
    try {
      const { data } = await api.patch(`/contractor/purchase-orders/${id}/lines/${i}/taxable`, { taxable });
      setPo(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update the line");
    }
  };

  const openSend = () => { setSendEmail(!!vendorEntry?.contact_email); setShowSend(true); };

  const confirmSend = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/contractor/purchase-orders/${id}/send`, { send_email: sendEmail && !!vendorEntry?.contact_email });
      toast.success(data.email_sent ? "Sent to vendor and emailed the PDF" : "Sent to vendor");
      setShowSend(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const openManual = () => { setManualForm(emptyManualForm); setShowManual(true); };

  const submitManual = async () => {
    const t = manualForm.type;
    let details = {};
    if (t === "backordered") {
      if (manualForm.lineRefs.length === 0) return toast.error("Select at least one line item");
      details = { line_refs: manualForm.lineRefs.map(Number), estimated_date: manualForm.estimatedDate || null };
    } else if (t === "price_changed") {
      if (manualForm.lineRef === "" || !manualForm.newPrice) return toast.error("Select a line and enter the new price");
      details = { line_ref: Number(manualForm.lineRef), new_price: Number(manualForm.newPrice) };
    } else if (t === "substitution_proposed") {
      if (manualForm.lineRef === "" || !manualForm.proposedItem) return toast.error("Select a line and describe the substitution");
      details = { line_ref: Number(manualForm.lineRef), proposed_item: manualForm.proposedItem };
    } else if (t === "delivery_date_confirmed") {
      if (!manualForm.date) return toast.error("Enter the confirmed date");
      details = { date: manualForm.date };
    } else if (t === "shipped") {
      if (!manualForm.trackingNumber) return toast.error("Enter a tracking number");
      details = { tracking_number: manualForm.trackingNumber, carrier: manualForm.carrier || null, expected_delivery_date: manualForm.shipDate || null };
    }
    try {
      await api.post(`/contractor/purchase-orders/${id}/vendor-response`, { type: t, details, note: manualForm.note || null });
      toast.success("Response recorded");
      setShowManual(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to record response");
    }
  };

  const resolveResponse = async (responseId, action) => {
    try {
      await api.post(`/contractor/purchase-orders/${id}/responses/${responseId}/resolve`, { action });
      toast.success(action === "accept" ? "Accepted — PO updated" : "Declined");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to resolve");
    }
  };

  const toggleLineRef = (idx) => {
    const s = String(idx);
    setManualForm((f) => ({ ...f, lineRefs: f.lineRefs.includes(s) ? f.lineRefs.filter((x) => x !== s) : [...f.lineRefs, s] }));
  };

  const openDelivery = () => {
    setDeliveryForm({
      expected_delivery: po.expected_delivery ? po.expected_delivery.slice(0, 10) : "",
      delivery_instructions: po.delivery_instructions || "",
      site_contact_name: po.site_contact_name || "",
      site_contact_phone: po.site_contact_phone || "",
    });
    setShowDelivery(true);
  };

  const submitDelivery = async () => {
    setSavingDelivery(true);
    try {
      await api.patch(`/contractor/purchase-orders/${id}/delivery-info`, {
        expected_delivery: deliveryForm.expected_delivery || null,
        delivery_instructions: deliveryForm.delivery_instructions || null,
        site_contact_name: deliveryForm.site_contact_name || null,
        site_contact_phone: deliveryForm.site_contact_phone || null,
      });
      toast.success("Delivery info updated");
      setShowDelivery(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update delivery info");
    } finally {
      setSavingDelivery(false);
    }
  };

  const clearCreditFlag = async () => {
    try {
      await api.post(`/contractor/purchase-orders/${id}/clear-credit-flag`);
      toast.success("Credit flag cleared");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to clear credit flag");
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-5xl" data-testid="po-detail-page">
      <Link to="/contractor/purchase-orders" className="text-[12.5px] text-slate-500 flex items-center gap-1 mb-3"><ChevronLeft size={13} /> Back to Purchase Orders</Link>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-extrabold tracking-tight mono">{po.po_number}</h1>
            <StatusBadge status={po.status} />
          </div>
          <p className="text-slate-500 text-sm mt-1">{po.vendor_name} · Expected {po.expected_delivery ? fmtDate(po.expected_delivery) : "TBD"}</p>
          <div className="text-slate-500 text-sm mt-0.5 flex items-center gap-2 flex-wrap" data-testid="po-payment">
            Payment: <span className="font-medium text-slate-700">{po.payment_method === "ramp" ? "Ramp" : po.payment_method === "card" ? "Card" : po.payment_method === "on_account" ? "On account" : "Not set"}</span>
            {canManagePO && !["card", "on_account"].includes(po.payment_method) && !["cancelled", "invoiced", "closed"].includes(po.status) && (
              <button onClick={() => setRamp(po.payment_method !== "ramp")} data-testid="toggle-ramp-payment"
                className="px-2 py-0.5 rounded border border-slate-200 text-[11.5px] font-semibold hover:bg-slate-50">
                {po.payment_method === "ramp" ? "Remove Ramp" : "Pay with Ramp"}
              </button>
            )}
          </div>
          <p className="text-slate-500 text-sm mt-0.5" data-testid="po-job">Job: {po.job_code ? <span className="font-medium text-slate-700">{po.job_code} — {po.job_name}</span> : "—"}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={downloadPdf} data-testid="download-pdf-button" className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg border border-slate-200 text-[13px] font-semibold hover:bg-slate-50">
            <Download size={14} /> Download PDF
          </button>
          {po.status === "draft" && canManagePO && (
            <button onClick={openSend} data-testid="mark-as-sent-button" className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[13px] font-semibold">
              <Send size={14} /> Send to Vendor
            </button>
          )}
          {po.status !== "draft" && canManagePO && (
            <button onClick={openManual} data-testid="record-vendor-response-button" className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg border border-slate-200 text-[13px] font-semibold hover:bg-slate-50">
              <MessageSquarePlus size={14} /> Record Vendor Response
            </button>
          )}
          {canManagePO && po.status === "draft" && (
            <button onClick={() => setConfirmRemove("delete")} data-testid="delete-po-button" className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg border border-slate-200 text-[13px] font-semibold text-red-600 hover:bg-red-50">
              <Trash2 size={14} /> Delete
            </button>
          )}
          {canManagePO && ["sent", "acknowledged", "in_progress", "rejected"].includes(po.status) && (
            <button onClick={() => setConfirmRemove("cancel")} data-testid="cancel-po-button" className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg border border-slate-200 text-[13px] font-semibold text-red-600 hover:bg-red-50">
              <Ban size={14} /> Cancel PO
            </button>
          )}
          {canManagePO && (
            <button onClick={openDelivery} data-testid="edit-delivery-info-button" className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg border border-slate-200 text-[13px] font-semibold hover:bg-slate-50">
              <Truck size={14} /> Delivery Info
            </button>
          )}
        </div>
      </div>

      {po.status === "cancelled" && (
        <div className="mt-4 text-[13px] text-red-900 bg-red-50 border border-red-100 rounded-lg px-3.5 py-3" data-testid="po-cancelled-banner">
          <span className="font-semibold">Cancelled</span>{po.cancelled_by ? ` by ${po.cancelled_by}` : ""}{po.cancelled_reason ? ` — ${po.cancelled_reason}` : ""}
        </div>
      )}

      <ConfirmAction open={!!confirmRemove} onOpenChange={(o) => !o && setConfirmRemove(null)}
        title={confirmRemove === "cancel" ? `Cancel ${po.po_number}?` : `Delete ${po.po_number}?`}
        description={confirmRemove === "cancel" ? "The vendor is notified with your reason. The PO stays on record as cancelled." : "It was never sent, so it's removed completely. The audit trail keeps a record."}
        confirmLabel={confirmRemove === "cancel" ? "Cancel PO" : "Delete"} requireReason={confirmRemove === "cancel"}
        reasonPlaceholder="Why is it cancelled? (the vendor sees this)" testId="remove-po-dialog"
        onConfirm={async (reason) => {
          if (confirmRemove === "cancel") {
            await api.post(`/contractor/purchase-orders/${po.id}/cancel`, { reason });
            toast.success(`${po.po_number} cancelled — the vendor was notified`);
            load();
          } else {
            await api.delete(`/contractor/purchase-orders/${po.id}`);
            toast.success(`${po.po_number} deleted`);
            navigate("/contractor/purchase-orders");
          }
        }} />

      {po.tracking_number && (
        <div className="mt-4 flex items-center justify-between gap-3 text-[13px] bg-sky-50 border border-sky-100 rounded-lg px-3.5 py-3" data-testid="tracking-banner">
          <div>
            <span className="text-slate-500">Tracking:</span> <span className="font-semibold mono">{po.tracking_number}</span>
            {po.carrier && <span className="text-slate-500"> · {po.carrier}</span>}
          </div>
          <button
            onClick={() => { navigator.clipboard?.writeText(po.tracking_number); toast.success("Tracking number copied"); }}
            data-testid="copy-tracking-button"
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-sky-200 text-[12px] font-semibold hover:bg-sky-100"
          >
            <Copy size={12} /> Copy
          </button>
        </div>
      )}

      {po.needs_credit && (
        <div className="mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-[13px] text-red-900 bg-red-50 border border-red-100 rounded-lg px-3.5 py-3" data-testid="needs-credit-banner">
          <div className="flex items-start gap-2">
            <AlertTriangle size={15} className="shrink-0 mt-0.5" />
            <div>Damaged or missing items were reported on this delivery — a credit is pending until resolved.</div>
          </div>
          {(user?.role === "accounting" || user?.role === "company_admin") && (
            <button onClick={clearCreditFlag} data-testid="clear-credit-flag-button" className="flex items-center justify-center gap-1 shrink-0 h-9 px-3 rounded-lg border border-red-200 text-[12px] font-semibold hover:bg-red-100">
              <CircleCheck size={13} /> Clear Flag
            </button>
          )}
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-8 mt-7">
        <div className="md:col-span-2">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]" data-testid="po-items-table">
              <thead>
                <tr className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold border-b border-slate-100">
                  <th className="text-left py-2">#</th><th className="text-left py-2">Description</th><th className="text-right py-2">Qty</th><th className="text-right py-2">Unit Price</th><th className="text-center py-2">Taxable</th><th className="text-right py-2">Line Total</th>
                </tr>
              </thead>
              <tbody>
                {po.items.map((it, i) => (
                  <tr key={i} className="border-b border-slate-50">
                    <td className="py-2.5 text-slate-400">{i + 1}</td>
                    <td className="py-2.5">{it.description}</td>
                    <td className="py-2.5 text-right">{it.qty} {it.uom || it.unit}</td>
                    <td className="py-2.5 text-right mono">{fmt(it.unit_price)}</td>
                    <td className="py-2.5 text-center">
                      <input type="checkbox" checked={!!it.taxable} disabled={!canManagePO || TAX_LOCKED.includes(po.status)} onChange={(e) => setLineTaxable(i, e.target.checked)} data-testid={`po-line-taxable-${i}`} />
                    </td>
                    <td className="py-2.5 text-right mono font-medium">{fmt(it.extended_price ?? it.qty * it.unit_price)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                {/* Taxable lines are taxed at the job's tax rate. */}
                {po.tax_total > 0 && <tr><td colSpan={5} className="text-right py-1.5 text-slate-500">Subtotal</td><td className="text-right py-1.5 mono">{fmt(po.subtotal ?? po.amount)}</td></tr>}
                {po.tax_total > 0 && <tr><td colSpan={5} className="text-right py-1.5 text-slate-500">Tax</td><td className="text-right py-1.5 mono" data-testid="po-tax-total">{fmt(po.tax_total)}</td></tr>}
                <tr><td colSpan={5} className="text-right py-2 font-bold">Total</td><td className="text-right py-2 font-bold mono">{fmt(po.amount)}</td></tr>
              </tfoot>
            </table>
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[13px] text-slate-500">
            <div>Delivery status: <span className="font-medium text-slate-700 capitalize">{po.delivery_status.replace(/_/g, " ")}</span></div>
            <div>Receiving: <StatusBadge status={po.receiving_status} testId="receiving-status-badge" /></div>
          </div>
          {(po.delivery_instructions || po.site_contact_name) && (
            <div className="mt-3 text-[12.5px] text-slate-600 bg-slate-50 rounded-lg p-3" data-testid="delivery-info-block">
              {po.site_contact_name && <div><span className="font-semibold">Site contact:</span> {po.site_contact_name}{po.site_contact_phone ? ` · ${po.site_contact_phone}` : ""}</div>}
              {po.delivery_instructions && <div className="mt-1">{po.delivery_instructions}</div>}
            </div>
          )}

          <ReceivingPanel po={po} onUpdate={load} />

          <div className="mt-8">
            <div className="text-[14.5px] font-bold mb-3">Vendor Response Timeline</div>
            <div className="space-y-2.5" data-testid="response-timeline">
              {(po.responses || []).length === 0 && <div className="text-slate-400 text-[13px]">No vendor responses yet.</div>}
              {(po.responses || []).slice().reverse().map((r) => {
                const meta = responseTypeMeta(r.type);
                const Icon = meta.icon;
                const canResolve = canManagePO && (r.type === "price_changed" || r.type === "substitution_proposed") && !r.resolution;
                return (
                  <div key={r.id} className="border border-slate-200 rounded-xl p-3" data-testid={`response-row-${r.id}`}>
                    <div className="flex items-center gap-2">
                      <Icon size={15} className={meta.color} />
                      <span className="font-semibold text-[13px]">{meta.label}</span>
                      <span className="text-[11px] text-slate-400">· {fmtDateTime(r.created_at)}</span>
                      <span className="text-[11px] text-slate-400">· {r.recorded_via === "manual" ? `recorded by ${r.recorded_by}` : "via vendor portal"}</span>
                    </div>
                    {r.type === "backordered" && (
                      <div className="text-[12.5px] text-slate-600 mt-1.5">
                        Line(s) {(r.details.line_refs || []).map((n) => n + 1).join(", ")} · est. {fmtDate(r.details.estimated_date, "TBD")}
                      </div>
                    )}
                    {r.type === "price_changed" && (
                      <div className="text-[12.5px] text-slate-600 mt-1.5">
                        Line {r.details.line_ref + 1}: new price {fmt(r.details.new_price)}
                      </div>
                    )}
                    {r.type === "substitution_proposed" && (
                      <div className="text-[12.5px] text-slate-600 mt-1.5">
                        Line {r.details.line_ref + 1}: proposed "{r.details.proposed_item}"
                      </div>
                    )}
                    {r.type === "delivery_date_confirmed" && (
                      <div className="text-[12.5px] text-slate-600 mt-1.5">Confirmed for {r.details.date}</div>
                    )}
                    {r.type === "shipped" && (
                      <div className="text-[12.5px] text-slate-600 mt-1.5">
                        {r.details.tracking_number}{r.details.carrier ? ` · ${r.details.carrier}` : ""}{r.details.expected_delivery_date ? ` · ETA ${fmtDate(r.details.expected_delivery_date)}` : ""}
                      </div>
                    )}
                    {r.note && <div className="text-[12.5px] text-slate-500 mt-1 italic">"{r.note}"</div>}
                    {r.resolution && (
                      <div className={`text-[11.5px] font-semibold mt-2 ${r.resolution.action === "accepted" ? "text-emerald-600" : "text-slate-500"}`}>
                        {r.resolution.action === "accepted" ? "Accepted" : "Declined"} by {r.resolution.by} · {fmtDateTime(r.resolution.at)}
                      </div>
                    )}
                    {canResolve && (
                      <div className="flex gap-2 mt-2.5">
                        <button onClick={() => resolveResponse(r.id, "accept")} data-testid={`accept-response-${r.id}`} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-[12px] font-semibold">
                          <Check size={12} /> Accept &amp; Update PO
                        </button>
                        <button onClick={() => resolveResponse(r.id, "decline")} data-testid={`decline-response-${r.id}`} className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 text-[12px] font-semibold hover:bg-slate-50">
                          <X size={12} /> Decline
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {invoices.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-100 p-4" data-testid="po-invoices-panel">
              <div className="text-[13px] font-bold">Invoices</div>
              <div className="mt-2 space-y-1.5">
                {invoices.map((inv) => (
                  <button key={inv.id} onClick={() => navigate(`/contractor/invoices/${inv.id}`)}
                    className="w-full flex items-center justify-between text-[12.5px] rounded-lg border border-slate-100 px-3 py-2 hover:bg-slate-50">
                    <span className="mono text-sky-600 font-medium">{inv.invoice_number}</span>
                    <span className="text-slate-500">${Number(inv.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                    <StatusBadge status={inv.status} />
                  </button>
                ))}
              </div>
            </div>
          )}
          <DocumentsPanel entityType="purchase_order" entityId={po.id} />
        </div>
        <div>
          <ChatPanel contractorTenantId={user?.tenant_id} vendorId={po.vendor_id} subjectType="po" subjectId={po.id} subjectLabel={po.po_number} />
        </div>
      </div>

      <Dialog open={showSend} onOpenChange={setShowSend}>
        <DialogContent data-testid="send-po-modal">
          <DialogHeader><DialogTitle>Send to Vendor</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-[12.5px] text-slate-500">Sending to the vendor is always enough — emailing the PDF is optional.</p>
            <div className="border border-slate-200 rounded-lg p-3 text-[12.5px]">
              <div className="font-medium text-slate-700">{po.vendor_name}</div>
              {vendorEntry?.contact_email ? (
                <div className="text-slate-500 mt-0.5">
                  {vendorEntry.contact_email}
                  {vendorEntry.vendor_status === "unregistered" && (
                    <div className="text-slate-400 mt-0.5" data-testid="send-po-not-on-scapesource">Not on ScapeSource yet — the email includes a link to join the network free.</div>
                  )}
                </div>
              ) : (
                <div className="text-amber-600 mt-0.5">No contact email on file — email delivery unavailable.</div>
              )}
            </div>
            <label className="flex items-center gap-2 text-[13px]">
              <input type="checkbox" checked={sendEmail} disabled={!vendorEntry?.contact_email} onChange={(e) => setSendEmail(e.target.checked)} data-testid="send-email-toggle" />
              Also email the PDF to the vendor
            </label>
            <button disabled={sending} onClick={confirmSend} data-testid="confirm-send-button" className="w-full h-10 rounded-lg bg-sky-500 text-white text-[13px] font-semibold disabled:opacity-60">
              {sending ? "Sending…" : "Send to Vendor"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showManual} onOpenChange={setShowManual}>
        <DialogContent data-testid="manual-response-modal">
          <DialogHeader><DialogTitle>Record Vendor Response</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-[12.5px] text-slate-500">For a response received by phone or email — record it on the vendor's behalf.</p>
            <div>
              <label className="text-[13px] font-medium">Response Type</label>
              <SearchableSelect value={manualForm.type} onChange={(e) => setManualForm({ ...emptyManualForm, type: e.target.value })} data-testid="manual-response-type-select" className="mt-1.5 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm">
                {RESPONSE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </SearchableSelect>
            </div>

            {manualForm.type === "backordered" && (
              <div>
                <label className="text-[13px] font-medium">Backordered Line(s)</label>
                <div className="mt-1.5 space-y-1 max-h-32 overflow-y-auto ss-scrollbar">
                  {po.items.map((it, i) => (
                    <label key={i} className="flex items-center gap-2 text-[12.5px]">
                      <input type="checkbox" checked={manualForm.lineRefs.includes(String(i))} onChange={() => toggleLineRef(i)} data-testid={`backorder-line-${i}`} />
                      {it.description}
                    </label>
                  ))}
                </div>
                <input type="date" value={manualForm.estimatedDate} onChange={(e) => setManualForm({ ...manualForm, estimatedDate: e.target.value })} data-testid="backorder-eta-input" className="mt-2 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" placeholder="Estimated availability" />
              </div>
            )}

            {manualForm.type === "price_changed" && (
              <div className="grid grid-cols-2 gap-2">
                <SearchableSelect value={manualForm.lineRef} onChange={(e) => setManualForm({ ...manualForm, lineRef: e.target.value })} data-testid="price-change-line-select" className="h-10 px-3 rounded-lg border border-slate-200 text-sm">
                  <option value="">Line…</option>
                  {po.items.map((it, i) => <option key={i} value={i}>{i + 1}. {it.description}</option>)}
                </SearchableSelect>
                <input type="number" min="0" step="0.01" value={manualForm.newPrice} onChange={(e) => setManualForm({ ...manualForm, newPrice: e.target.value })} placeholder="New unit price" data-testid="price-change-value-input" className="h-10 px-3 rounded-lg border border-slate-200 text-sm" />
              </div>
            )}

            {manualForm.type === "substitution_proposed" && (
              <div className="space-y-2">
                <SearchableSelect value={manualForm.lineRef} onChange={(e) => setManualForm({ ...manualForm, lineRef: e.target.value })} data-testid="substitution-line-select" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm">
                  <option value="">Line…</option>
                  {po.items.map((it, i) => <option key={i} value={i}>{i + 1}. {it.description}</option>)}
                </SearchableSelect>
                <input value={manualForm.proposedItem} onChange={(e) => setManualForm({ ...manualForm, proposedItem: e.target.value })} placeholder="Proposed substitute item" data-testid="substitution-item-input" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
              </div>
            )}

            {manualForm.type === "delivery_date_confirmed" && (
              <input type="date" value={manualForm.date} onChange={(e) => setManualForm({ ...manualForm, date: e.target.value })} data-testid="delivery-date-input" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            )}

            {manualForm.type === "shipped" && (
              <div className="space-y-2">
                <input value={manualForm.trackingNumber} onChange={(e) => setManualForm({ ...manualForm, trackingNumber: e.target.value })} placeholder="Tracking number" data-testid="tracking-number-input" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
                <div className="grid grid-cols-2 gap-2">
                  <input value={manualForm.carrier} onChange={(e) => setManualForm({ ...manualForm, carrier: e.target.value })} placeholder="Carrier (optional)" data-testid="carrier-input" className="h-10 px-3 rounded-lg border border-slate-200 text-sm" />
                  <input type="date" value={manualForm.shipDate} onChange={(e) => setManualForm({ ...manualForm, shipDate: e.target.value })} data-testid="ship-eta-input" className="h-10 px-3 rounded-lg border border-slate-200 text-sm" />
                </div>
              </div>
            )}

            <textarea value={manualForm.note} onChange={(e) => setManualForm({ ...manualForm, note: e.target.value })} placeholder="Note (optional)" rows={2} data-testid="manual-response-note-input" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
            <button onClick={submitManual} data-testid="confirm-manual-response-button" className="w-full h-10 rounded-lg bg-sky-500 text-white text-[13px] font-semibold">Save Response</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showDelivery} onOpenChange={setShowDelivery}>
        <DialogContent data-testid="delivery-info-modal">
          <DialogHeader><DialogTitle>Delivery Info</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-[13px] font-medium">Expected Delivery</label>
              <input type="date" value={deliveryForm.expected_delivery} onChange={(e) => setDeliveryForm({ ...deliveryForm, expected_delivery: e.target.value })} data-testid="delivery-expected-date-input" className="mt-1.5 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[13px] font-medium">Site Contact</label>
                <input value={deliveryForm.site_contact_name} onChange={(e) => setDeliveryForm({ ...deliveryForm, site_contact_name: e.target.value })} data-testid="delivery-site-contact-name-input" className="mt-1.5 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
              </div>
              <div>
                <label className="text-[13px] font-medium">Contact Phone</label>
                <input value={deliveryForm.site_contact_phone} onChange={(e) => setDeliveryForm({ ...deliveryForm, site_contact_phone: e.target.value })} data-testid="delivery-site-contact-phone-input" className="mt-1.5 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
              </div>
            </div>
            <div>
              <label className="text-[13px] font-medium">Delivery Instructions</label>
              <textarea value={deliveryForm.delivery_instructions} onChange={(e) => setDeliveryForm({ ...deliveryForm, delivery_instructions: e.target.value })} rows={3} data-testid="delivery-instructions-input" className="mt-1.5 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
            </div>
            {(po.expected_delivery_history || []).length > 0 && (
              <div className="text-[11.5px] text-slate-500" data-testid="expected-delivery-history">
                <div className="font-semibold uppercase tracking-wide text-[10.5px] text-slate-400 mb-1">Date History</div>
                {po.expected_delivery_history.slice().reverse().map((h, i) => (
                  <div key={i}>{fmtDate(h.date)} · {h.changed_via === "vendor_response" ? "confirmed by vendor" : `set by ${h.changed_by}`}</div>
                ))}
              </div>
            )}
            <button disabled={savingDelivery} onClick={submitDelivery} data-testid="confirm-delivery-info-button" className="w-full h-10 rounded-lg bg-sky-500 text-white text-[13px] font-semibold disabled:opacity-60">
              {savingDelivery ? "Saving…" : "Save Delivery Info"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
